<template>
  <div>
    <el-card class="chart-card">
      <h1>PignooReader - 数据读取器</h1>
      <p>
        <el-text>PignooReader是使用Pignoo时最重要的接口（Interface）。</el-text>
        <br />
        <el-text>PignooReader是一个数据只读集，用于读取数据。</el-text>
        <br />
        <el-text>PignooReader的方法参考java.util.List与java.util.stream.Stream，以达到尽可能接近原生List的体验。</el-text>
        <el-text>但是，还是有一些用法不同的地方，使用时需要尤其注意。</el-text>
      </p>
      <h1>中间函数</h1>
      <p>
        <el-text>中间函数 - 是指设置过滤方法、排序方法等。</el-text>
        <el-text>类似list.stream().filter(...)，用于给集合施加过滤条件。</el-text>
        <el-text>中间函数的明显特征，返回结果是PignooReader自身，可以进行链式调用。</el-text>
      </p>
      <p>
        <el-text>注意：与Stream不同的是，</el-text>
        <el-text tag="b">PignooReader是可变的，中间函数会改变PignooReader，返回结果是PignooReader自身。</el-text>
        <el-text>所以，返回自身的主要意义是为了可以链式调用。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code1 }}</code></pre>
      </div>
      <h1>PignooReader的不可变用法</h1>
      <p>
        <el-text>PignooReader的copy方法，可以复制一个新的PignooReader。显式使用copy，可以达到类似Stream不可变的用法。</el-text>
      </p>
      <p>
        <el-text>copy本质上是“从当前条件复制一份”，于是可以在任何中间阶段copy，已达到暂存当前过滤条件的目的。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code2 }}</code></pre>
      </div>
      <h1>NULL的三值问题</h1>
      <p>
        <el-text>Pignoo将SQL中NULL的三值逻辑转为了贴近Java思维的二值逻辑。</el-text>
      </p>
      <p>
        <el-text>绝大多数主流SQL数据库，都是采用的“三值逻辑”处理NULL。例如：name字段是NULL，则在WHERE name='小猪'和WHERE name!='小猪'均无法匹配到NULL。</el-text>
        <el-text tag="b">Pignoo在处理时，将filter的判断条件翻译成了二值条件！例如：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code3 }}</code></pre>
      </div>
      <p>
        <el-text tag="b">特别注意，IN这类多参数filter，传入的集合、数组请一定提前判空；如果传入了null，Pignoo会导致按照IS NULL处理！</el-text>
      </p>
      <h1>查询条件与排序条件</h1>
      <p>
        <el-text>针对查询条件与排序条件，Pignoo做了高度类似List操作的处理。在使用Pignoo时，请尽可能按照List操作的直觉去操作reader。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code4 }}</code></pre>
      </div>
      <p>
        <el-text>下面是条件枚举与“糖”字符的对照表（可以参考FMode源码），糖是不区分大小写的。</el-text>
      </p>
      <div style="width: 600px;margin: auto;">
        <el-table :data="fmodeTable" style="width: 100%;" size="small" border="true">
          <el-table-column prop="fmode" label="FMode枚举" />
          <el-table-column prop="string" label="String“糖”" />
          <el-table-column prop="note" label="解释" />
        </el-table>
      </div>
      <p>
        <el-text>连续的filter调用，每次调用都是“在上次筛选的结果基础上，进一步筛选”。所以，从结果上看，</el-text>
        <el-text tag="b">多个filter最终会形成连续AND拼接的SQL语句。</el-text>
        <el-text>这也是开发过程中最常用的筛选方式。</el-text>
        <el-text>如果想使用OR条件，写法如下：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code5 }}</code></pre>
      </div>
      <p>
        <el-text>排序仅支持每次按一个字段排序，且排序也做了类Stream化处理：</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code6 }}</code></pre>
      </div>
      <p>
        <el-text>如果你还是在用SQL思路看待问题，很可能会误认为上面代码是“先按ID排序，再按Name排序”。</el-text>
        <el-text>但设想一下，你如果是用同样的流程用.stream()操作List，第二次排序时候，会将第一次的排序“打乱”，最终形成Name顺序优先、ID顺序其次的结果。</el-text>
        <el-text>Pignoo模拟了这种思路，最终形成了ORDER BY name ASC, id DESC的效果。</el-text>
      </p>
      <h1>终结函数与惰性执行</h1>
      <p>
        <el-text>终结函数 - 是指PignooReader最终执行SQL查询的操作。例如get、getAll、size。</el-text>
      </p>
      <p>
        <el-text>PignooReader是惰性执行的，在filter阶段只对过滤条件进行缓存，实际上没有任何的SQL被执行。</el-text>
        <el-text>直到调用终结函数时，才会真的提交SQL。</el-text>
        <el-text>这样做，每次pignoo.reader(Pig.class)执行得到的“list”，虽代表整张表的数据，</el-text>
        <el-text>但不会真的把整张表加载到JVM内存中。也是因此，Pignoo无法使用真正的List，而是靠SQL尽可能模拟List操作。</el-text>
      </p>
      <div>
        <pre><code class="language-java">{{ code7 }}</code></pre>
      </div>
      <p>
        <el-text>注意：当返回集合（java.util.List）时，必然不是NULL，但可能是0个元素。当返回JavaBean时，可能为NULL。</el-text>
        <el-text>这一点和大部分ORM类似。所以，请在必要时进行非空判断。</el-text>
      </p>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'

onMounted(() => {
  if (window.hljs) {
    window.hljs.highlightAll()
  }
})

const code1 = `gru.run(pignoo -> { // 使用Gru正确得获取一个pignoo，下面例子将忽略这一步
  PignooReader list = pignoo.reader(Pig.class); // 这里把reader变量叫list，方便初学者理解

  list.filter(Pig::getId, ">=", 3L)
      .filter(Pig::getName, "like", "%猪%"); // 中间函数，返回自身，链式嗲用

  list.sort(Pig::getId, SMode.MAX_FIRST); // 中间函数

  List<Pig> pigs = list.getAll(); // 最终：按ID从大到小，查询全部【id>=3】且【name like %猪%】的数据
  System.out.println(pigs);
});`

const code2 = `// 下面两个筛选相互独立，互不影响
list.copyReader().filter(Pig::getId, ">=", 3L).getAll();
list.copyReader().filter(Pig::getName, "like", "%猪%").getAll();`

const code3 = `list.filter(Pig::getName, "!=", "小猪"); // WHERE name!='小猪' OR name IS NULL

String param = null; // 由于filter不允许直接承接单参数的null，但可以允许传入变量是null
list.filter(Pig::getName, "==", param); // WHERE name IS NULL
list.filter(Pig::getName, "!=", param); // WHERE name IS NOT NULL
list.filter(Pig::getName, "like", param); // 报错
list.filter(Pig::getName, "not like", param); // 报错
list.filter(Pig::getName, "not like", "%小猪%"); // WHERE name NOT LIKE '%小猪%' OR name IS NULL

list.filter(Pig::getName, "not in", "乔治", "佩奇"); // WHERE name not in ('乔治', '佩奇') OR name IS NULL
list.filter(Pig::getName, "in", "乔治", "佩奇"); // WHERE name in ('乔治', '佩奇')
list.filter(Pig::getName, "not in", "乔治", "佩奇", null); // WHERE name not in ('乔治', '佩奇')
list.filter(Pig::getName, "in", "乔治", "佩奇", null); // WHERE name in ('乔治', '佩奇') OR name IS NULL

list.filter(Pig::getName, "in", Arrays.asList()); // WHERE 1=0
list.filter(Pig::getName, "not in", Arrays.asList()); // WHERE 1=1`

const code4 = `list.filter(Pig::getId, ">=", 3L); // “糖”形式的条件公式
list.filter(Pig::getId, FMode.LE, 10L); // 枚举形式的条件公式，每个枚举都有一个对应的“糖”

list.filter(Pig::getId, "in", 4L, 5L, 6L); // 多参数条件
Set<Long> ids = new HashSet<>();
ids.add(4L);
ids.add(5L);
ids.add(6L);
list.filter(Pig::getId, "in", ids, 7L, 8L); // 多参数条件也支持使用集合，甚至可以混用
// 注意：集合与非集合混用时，会按Object处理，如果集合、数组是null，则会解释为匹配对象是null，而非集合是空
// 如果担心用错，那么请不好将集合、可变长参数混用！用集合就只传单集合；用可变长参数，每个元素都要保证是目标对象而不是集合！

list.filter(Pig::getName, "not in", "小猪", "大猪"); // 能匹配到NULL，对SQL的“三值逻辑”做了处理
list.filter(Pig::getName, "not in", "小猪", "大猪", null); // 这样可以排除NULL
list.filter(Pig::getName, "!=", null); // 这样可以仅排除NULL

list.filter(param.getId() != null, Pig::getId, "==", param.getId()); // 满足特定条件（param.id != null），再执行过滤`

const fmodeTable = [
  { "fmode": "EQ", "string": "==", "note": "等于" },
  { "fmode": "NE", "string": "!=", "note": "不等于" },
  { "fmode": "GT", "string": ">", "note": "大于" },
  { "fmode": "LT", "string": "<", "note": "小于" },
  { "fmode": "GE", "string": ">=", "note": "大于等于" },
  { "fmode": "LE", "string": "<=", "note": "小于等于" },
  { "fmode": "LIKE", "string": "like", "note": "模糊匹配" },
  { "fmode": "NOT_LIKE", "string": "not like", "note": "反模糊匹配" },
  { "fmode": "IN", "string": "in", "note": "包含，多参数" },
  { "fmode": "NOT_IN", "string": "not in", "note": "不包含，多参数" },
]

const code5 = `// 多个filter用OR组合成一个filter后，在reader上应用一次条件
list.filter(f -> 
    f.or(Test01::getId, ">=", 10L)
     .or(Test01::getName, "!=" , null)
);`

const code6 = `list.sort(Pig::getId, SMode.MAX_FIRST) // 按照ID排序，小的在前
    .sort(Pig::getName, SMode.MIN_FIRST) // 按照Name字典序排序，小的在前
    .getAll(); // 查询最终结果是：先按Name排序，同Name时再按ID排序`

const code7 = `list.filter(...); // 先应用一些过滤条件

list.size(); // 元素个数
list.getAll(); // 全部元素
list.get(5, 6); // 跳过5个，取出6个
list.getAny(); // 查询任一个（实际上会忽略排序条件，以更高效的方式查询满足条件的一个）
list.getFirst(); // 查询第一个
list.max(Pig::getId, Long.class); // 求某属性的最大值，与之对应还有min、sum、avg、countDistinct
list.sumNullAs(User::getMoney, BigDecimal.class, new BigDecimal(100)); // 求和，如果NULL，则使用100代替`

</script>

<style lang="less"></style>